import { focusRoundTrips, formatCount, formatMinutesSeconds } from "@context/shared";
import type { WritingMetrics } from "@context/shared";
import { MetricItem, type MetricTone } from "../common/MetricItem";
import type { StudentMessages } from "../../state/studentMessages";

const METRIC_DEFINITIONS: ReadonlyArray<{
  key: string;
  label: string;
  tone: MetricTone;
  tooltip: string;
  value: (metrics: WritingMetrics) => string;
}> = [
  {
    key: "wordCount",
    label: "Word count",
    tone: "metric-data",
    tooltip: "The current word count for the draft in the editor.",
    value: (m) => formatCount(m.wordCount),
  },
  {
    key: "elapsed",
    label: "Elapsed time",
    tone: "metric-active",
    tooltip:
      "Total time since this writing session started, including both active writing and idle time.",
    value: (m) => formatMinutesSeconds(m.totalElapsedMs),
  },
  {
    key: "active",
    label: "Active writing",
    tone: "metric-active",
    tooltip: "Time spent actively typing or editing in the writing editor.",
    value: (m) => formatMinutesSeconds(m.activeWritingMs),
  },
  {
    key: "idle",
    label: "Idle time",
    tone: "metric-data",
    tooltip: "Time when the session was open but you were not actively typing in the editor.",
    value: (m) => formatMinutesSeconds(m.idleMs),
  },
  {
    key: "keystrokes",
    label: "Keystrokes",
    tone: "metric-data",
    tooltip: "The number of keyboard presses recorded while working in this editor.",
    value: (m) => formatCount(m.keystrokeCount),
  },
  {
    key: "pasteEvents",
    label: "Paste events",
    tone: "metric-flag",
    tooltip: "How many times text was pasted into the writing editor during this session.",
    value: (m) => formatCount(m.pasteEvents),
  },
  {
    key: "pastedWords",
    label: "Pasted words",
    tone: "metric-flag",
    tooltip: "An estimate of how many words entered the editor through paste actions.",
    value: (m) => formatCount(m.pastedWords),
  },
  {
    key: "deletions",
    label: "Deletion activity",
    tone: "metric-data",
    tooltip: "The number of delete and backspace actions recorded while revising.",
    value: (m) => formatCount(m.deletionCount),
  },
  {
    key: "focus",
    label: "Focus changes",
    tone: "metric-data",
    tooltip:
      "One focus change is a completed round trip: entering the writing area and then leaving it. A visit that has not ended yet does not count.",
    value: (m) => formatCount(focusRoundTrips(m)),
  },
];

/**
 * The compact live writing summary, rendered as a strip inside the editor card
 * directly above the submit bar. The three reminder lines reuse the prompt's
 * compact guidance-line language so the tone cue stays consistent.
 */
export function LiveWritingSummary({
  metrics,
  messages,
}: {
  metrics: WritingMetrics;
  messages: StudentMessages;
}) {
  return (
    <section className="summary-panel" aria-labelledby="live-summary-heading">
      <h3 id="live-summary-heading" className="summary-heading">
        Live writing summary
      </h3>
      <div className="summary-strip">
        {METRIC_DEFINITIONS.map((definition) => (
          <MetricItem
            key={definition.key}
            label={definition.label}
            tone={definition.tone}
            tooltip={definition.tooltip}
            value={definition.value(metrics)}
          />
        ))}
      </div>
      <div className="summary-messages" aria-label="Writing summary reminders">
        <p className="guidance-line guidance-flag" data-testid="milestone-message">
          {messages.milestone}
        </p>
        <p className="guidance-line guidance-warn" data-testid="review-message">
          {messages.review}
        </p>
        <p className="guidance-line guidance-warn" data-testid="integrity-message">
          {messages.integrity}
        </p>
      </div>
    </section>
  );
}
